import { supabase } from '../lib/supabaseClient';
import type {
  AccountingPeriod,
  ApInvoice,
  ChartOfAccount,
  JournalEntry,
  JournalLine,
  PaymentOut,
  PayrollFact,
  PayrollLine,
  PayrollPeriod,
  PayrollRun,
  TrialBalanceRow,
  UUID,
} from '../types/domain';

/**
 * Finance (FASE 10 — Payroll & Finance Core, migration 0044) — reads and
 * writes wired to the real backend. Every write below calls a permission-
 * gated `_checked` RPC — never a direct `.insert()`/`.update()`. No sales/
 * cash/POS source exists anywhere in this schema (BLOCKED_BY_SOURCE) —
 * nothing here fabricates revenue, cash, or bank-reconciliation data.
 */

// ---------------------------------------------------------------------------
// Chart of Accounts
// ---------------------------------------------------------------------------

export async function listChartOfAccounts(): Promise<ChartOfAccount[]> {
  const { data, error } = await supabase
    .from('chart_of_accounts')
    .select('account_id, tenant_id, account_code, name, account_type, normal_balance, parent_account_id, is_postable, status')
    .order('account_code');
  if (error) throw new Error(`listChartOfAccounts failed: ${error.message}`);
  return (data ?? []) as ChartOfAccount[];
}

/** Calls fn_create_chart_of_account_checked (migration 0044, permission-gated on chart_of_account.create). */
export async function createChartOfAccount(
  accountCode: string,
  name: string,
  accountType: ChartOfAccount['account_type'],
  normalBalance: ChartOfAccount['normal_balance'],
  parentAccountId: UUID | null,
  isPostable: boolean
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_chart_of_account_checked', {
    p_account_code: accountCode,
    p_name: name,
    p_account_type: accountType,
    p_normal_balance: normalBalance,
    p_parent_account_id: parentAccountId,
    p_is_postable: isPostable,
  });
  if (error) throw new Error(`fn_create_chart_of_account_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_update_chart_of_account_checked (migration 0044, same permission as create). */
export async function updateChartOfAccount(
  accountId: UUID,
  name: string,
  parentAccountId: UUID | null,
  isPostable: boolean,
  status: ChartOfAccount['status']
): Promise<void> {
  const { error } = await supabase.rpc('fn_update_chart_of_account_checked', {
    p_account_id: accountId,
    p_name: name,
    p_parent_account_id: parentAccountId,
    p_is_postable: isPostable,
    p_status: status,
  });
  if (error) throw new Error(`fn_update_chart_of_account_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Accounting periods
// ---------------------------------------------------------------------------

export async function listAccountingPeriods(): Promise<AccountingPeriod[]> {
  const { data, error } = await supabase
    .from('accounting_periods')
    .select('period_id, tenant_id, period_code, start_date, end_date, status, closed_at, closed_by')
    .order('start_date', { ascending: false });
  if (error) throw new Error(`listAccountingPeriods failed: ${error.message}`);
  return (data ?? []) as AccountingPeriod[];
}

/** Calls fn_create_accounting_period_checked (migration 0044, permission-gated on accounting_period.create). Overlapping periods are rejected server-side. */
export async function createAccountingPeriod(periodCode: string, startDate: string, endDate: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_accounting_period_checked', {
    p_period_code: periodCode,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw new Error(`fn_create_accounting_period_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_close_period_checked (migration 0044, permission-gated on accounting_period.close). Blocked server-side if the period's trial balance is nonzero. */
export async function closeAccountingPeriod(periodId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_close_period_checked', { p_period_id: periodId });
  if (error) throw new Error(`fn_close_period_checked failed: ${error.message}`);
}

/** Calls fn_get_trial_balance (migration 0044) — read-time projection, never a stored row. */
export async function getTrialBalance(periodId: UUID): Promise<TrialBalanceRow[]> {
  const { data, error } = await supabase.rpc('fn_get_trial_balance', { p_period_id: periodId });
  if (error) throw new Error(`fn_get_trial_balance failed: ${error.message}`);
  return (data ?? []) as TrialBalanceRow[];
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

export async function listJournalEntries(limit = 50, branchId?: string): Promise<JournalEntry[]> {
  let query = supabase
    .from('journal_entries')
    .select('journal_id, tenant_id, branch_id, journal_no, entry_date, source_type, description, status, posted_at')
    .order('entry_date', { ascending: false })
    .limit(limit);
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listJournalEntries failed: ${error.message}`);
  return (data ?? []) as JournalEntry[];
}

export async function listJournalLines(journalIds: string[]): Promise<JournalLine[]> {
  if (journalIds.length === 0) return [];
  const { data, error } = await supabase
    .from('journal_lines')
    .select('line_id, tenant_id, journal_id, line_no, account_id, debit, credit, description')
    .in('journal_id', journalIds);
  if (error) throw new Error(`listJournalLines failed: ${error.message}`);
  return (data ?? []) as JournalLine[];
}

/** Calls fn_post_journal_entry_checked (migration 0044, permission-gated on journal_entry.post). Only accepts source_type MANUAL — AP/payroll postings go through their own dedicated functions below. Double-entry (sum debit = sum credit) is enforced server-side. */
export async function postManualJournalEntry(
  branchId: UUID,
  periodId: UUID,
  entryDate: string,
  description: string,
  lines: Array<{ account_id: UUID; debit: number; credit: number; description?: string }>
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_post_journal_entry_checked', {
    p_branch_id: branchId,
    p_period_id: periodId,
    p_entry_date: entryDate,
    p_source_type: 'MANUAL',
    p_source_id: null,
    p_description: description,
    p_lines: lines,
  });
  if (error) throw new Error(`fn_post_journal_entry_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_reverse_journal_entry_checked (migration 0044, permission-gated on journal_entry.reverse). Never edits the original entry — swaps debit/credit into a brand-new linked reversal entry. */
export async function reverseJournalEntry(journalId: UUID, periodId: UUID, entryDate: string, reason: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_reverse_journal_entry_checked', {
    p_journal_id: journalId,
    p_period_id: periodId,
    p_entry_date: entryDate,
    p_reason: reason,
  });
  if (error) throw new Error(`fn_reverse_journal_entry_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Payroll periods
// ---------------------------------------------------------------------------

export async function listPayrollPeriods(): Promise<PayrollPeriod[]> {
  const { data, error } = await supabase
    .from('payroll_periods')
    .select('payroll_period_id, tenant_id, period_code, start_date, end_date, status, locked_at, locked_by')
    .order('start_date', { ascending: false });
  if (error) throw new Error(`listPayrollPeriods failed: ${error.message}`);
  return (data ?? []) as PayrollPeriod[];
}

/** Calls fn_create_payroll_period_checked (migration 0044, permission-gated on payroll_period.create). Overlapping periods are rejected server-side. */
export async function createPayrollPeriod(periodCode: string, startDate: string, endDate: string): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_payroll_period_checked', {
    p_period_code: periodCode,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw new Error(`fn_create_payroll_period_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_lock_payroll_period_checked (migration 0044, permission-gated on payroll_period.lock). Blocked server-side while any DRAFT/APPROVED (not yet POSTED/VOID) payroll run remains in the period. */
export async function lockPayrollPeriod(payrollPeriodId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_lock_payroll_period_checked', { p_payroll_period_id: payrollPeriodId });
  if (error) throw new Error(`fn_lock_payroll_period_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Payroll facts
// ---------------------------------------------------------------------------

export async function listPayrollFacts(payrollPeriodId?: UUID, branchId?: string): Promise<PayrollFact[]> {
  let query = supabase
    .from('payroll_facts')
    .select('fact_id, tenant_id, branch_id, employee_id, payroll_period_id, fact_type, source_type, source_id, amount, note, status, created_at, created_by, approved_at, approved_by')
    .order('created_at', { ascending: false });
  if (payrollPeriodId) query = query.eq('payroll_period_id', payrollPeriodId);
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listPayrollFacts failed: ${error.message}`);
  return (data ?? []) as PayrollFact[];
}

/** Calls fn_record_payroll_fact_checked (migration 0044, permission-gated on payroll_fact.create). Amount is always human-entered — no salary structure exists in the canonical schema to compute it automatically. */
export async function recordPayrollFact(
  branchId: UUID,
  employeeId: UUID,
  payrollPeriodId: UUID,
  factType: PayrollFact['fact_type'],
  sourceType: PayrollFact['source_type'],
  sourceId: UUID | null,
  amount: number,
  note: string | null
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_record_payroll_fact_checked', {
    p_branch_id: branchId,
    p_employee_id: employeeId,
    p_payroll_period_id: payrollPeriodId,
    p_fact_type: factType,
    p_source_type: sourceType,
    p_source_id: sourceId,
    p_amount: amount,
    p_note: note,
  });
  if (error) throw new Error(`fn_record_payroll_fact_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_approve_payroll_fact_checked (migration 0044, permission-gated on payroll_fact.approve). DB-enforced maker-checker: the approver cannot be the same user who recorded the fact. */
export async function approvePayrollFact(factId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_payroll_fact_checked', { p_fact_id: factId });
  if (error) throw new Error(`fn_approve_payroll_fact_checked failed: ${error.message}`);
}

// ---------------------------------------------------------------------------
// Payroll runs
// ---------------------------------------------------------------------------

export async function listPayrollRuns(branchId?: string): Promise<PayrollRun[]> {
  let query = supabase
    .from('payroll_runs')
    .select('payroll_run_id, tenant_id, branch_id, payroll_period_id, run_no, status, journal_id, created_at, created_by, approved_at, approved_by')
    .order('created_at', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listPayrollRuns failed: ${error.message}`);
  return (data ?? []) as PayrollRun[];
}

export async function listPayrollLines(payrollRunId: UUID): Promise<PayrollLine[]> {
  const { data, error } = await supabase
    .from('payroll_lines')
    .select('payroll_line_id, tenant_id, payroll_run_id, employee_id, gross_amount, deduction_amount, bonus_amount, net_amount')
    .eq('payroll_run_id', payrollRunId);
  if (error) throw new Error(`listPayrollLines failed: ${error.message}`);
  return (data ?? []) as PayrollLine[];
}

/** Calls fn_create_payroll_run_checked (migration 0044, permission-gated on payroll_run.create). Duplicate run for the same branch/period is blocked server-side (uq_payroll_runs_tenant_branch_period). */
export async function createPayrollRun(branchId: UUID, payrollPeriodId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_payroll_run_checked', {
    p_branch_id: branchId,
    p_payroll_period_id: payrollPeriodId,
  });
  if (error) throw new Error(`fn_create_payroll_run_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_generate_payroll_lines_checked (migration 0044). Only sums status='APPROVED' payroll_facts — a DRAFT (unapproved) fact is never paid. */
export async function generatePayrollLines(payrollRunId: UUID): Promise<number> {
  const { data, error } = await supabase.rpc('fn_generate_payroll_lines_checked', { p_payroll_run_id: payrollRunId });
  if (error) throw new Error(`fn_generate_payroll_lines_checked failed: ${error.message}`);
  return data as number;
}

/** Calls fn_approve_payroll_run_checked (migration 0044, permission-gated on payroll_run.approve). DB-enforced maker-checker: the approver cannot be the same user who created the run. */
export async function approvePayrollRun(payrollRunId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_payroll_run_checked', { p_payroll_run_id: payrollRunId });
  if (error) throw new Error(`fn_approve_payroll_run_checked failed: ${error.message}`);
}

/** Calls fn_post_payroll_run_journal_checked (migration 0044, permission-gated on payroll_run.post). The three account IDs are chosen by the caller from the real chart of accounts — never silently picked. */
export async function postPayrollRunJournal(
  payrollRunId: UUID,
  accountingPeriodId: UUID,
  entryDate: string,
  salaryExpenseAccountId: UUID,
  deductionClearingAccountId: UUID,
  payrollPayableAccountId: UUID
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_post_payroll_run_journal_checked', {
    p_payroll_run_id: payrollRunId,
    p_accounting_period_id: accountingPeriodId,
    p_entry_date: entryDate,
    p_salary_expense_account_id: salaryExpenseAccountId,
    p_deduction_clearing_account_id: deductionClearingAccountId,
    p_payroll_payable_account_id: payrollPayableAccountId,
  });
  if (error) throw new Error(`fn_post_payroll_run_journal_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// AP invoices
// ---------------------------------------------------------------------------

export async function listApInvoices(branchId?: string): Promise<ApInvoice[]> {
  let query = supabase
    .from('ap_invoices')
    .select('invoice_id, tenant_id, branch_id, supplier_id, gr_id, invoice_no, invoice_date, due_date, amount, status, journal_id, created_by, approved_by')
    .order('due_date');
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listApInvoices failed: ${error.message}`);
  return (data ?? []) as ApInvoice[];
}

/** Calls fn_create_ap_invoice_checked (migration 0044, permission-gated on ap_invoice.create). If gr_id is supplied it must reference a real POSTED goods receipt in the same branch — no fake invoice generation. */
export async function createApInvoice(
  branchId: UUID,
  supplierId: UUID,
  grId: UUID | null,
  invoiceNo: string,
  invoiceDate: string,
  dueDate: string,
  amount: number
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_ap_invoice_checked', {
    p_branch_id: branchId,
    p_supplier_id: supplierId,
    p_gr_id: grId,
    p_invoice_no: invoiceNo,
    p_invoice_date: invoiceDate,
    p_due_date: dueDate,
    p_amount: amount,
  });
  if (error) throw new Error(`fn_create_ap_invoice_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_approve_ap_invoice_checked (migration 0044, permission-gated on ap_invoice.approve). DB-enforced maker-checker. */
export async function approveApInvoice(invoiceId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_ap_invoice_checked', { p_invoice_id: invoiceId });
  if (error) throw new Error(`fn_approve_ap_invoice_checked failed: ${error.message}`);
}

/** Calls fn_post_ap_invoice_journal_checked (migration 0044, permission-gated on ap_invoice.post). Expense/AP account IDs are chosen by the caller from the real chart of accounts. */
export async function postApInvoiceJournal(invoiceId: UUID, periodId: UUID, expenseAccountId: UUID, apAccountId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_post_ap_invoice_journal_checked', {
    p_invoice_id: invoiceId,
    p_period_id: periodId,
    p_expense_account_id: expenseAccountId,
    p_ap_account_id: apAccountId,
  });
  if (error) throw new Error(`fn_post_ap_invoice_journal_checked failed: ${error.message}`);
  return data as UUID;
}

// ---------------------------------------------------------------------------
// Payments out
// ---------------------------------------------------------------------------

export async function listPaymentsOut(branchId?: string): Promise<PaymentOut[]> {
  let query = supabase
    .from('payments_out')
    .select('payment_id, tenant_id, branch_id, ap_invoice_id, payment_no, payment_date, amount, method, status, journal_id, created_by, approved_by')
    .order('payment_date', { ascending: false });
  if (branchId) query = query.eq('branch_id', branchId);
  const { data, error } = await query;
  if (error) throw new Error(`listPaymentsOut failed: ${error.message}`);
  return (data ?? []) as PaymentOut[];
}

/** Calls fn_create_payment_out_checked (migration 0044, permission-gated on payment_out.create). Rejected server-side if the payment would exceed the invoice's remaining balance, or if the invoice hasn't been posted yet. */
export async function createPaymentOut(apInvoiceId: UUID, paymentDate: string, amount: number, method: PaymentOut['method']): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_payment_out_checked', {
    p_ap_invoice_id: apInvoiceId,
    p_payment_date: paymentDate,
    p_amount: amount,
    p_method: method,
  });
  if (error) throw new Error(`fn_create_payment_out_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_approve_ap_payment_checked (migration 0044, permission-gated on payment_out.approve). DB-enforced maker-checker. */
export async function approvePaymentOut(paymentId: UUID): Promise<void> {
  const { error } = await supabase.rpc('fn_approve_ap_payment_checked', { p_payment_id: paymentId });
  if (error) throw new Error(`fn_approve_ap_payment_checked failed: ${error.message}`);
}

/** Calls fn_post_ap_payment_journal_checked (migration 0044, permission-gated on payment_out.post). Marks the invoice PAID once fully paid. */
export async function postPaymentOutJournal(paymentId: UUID, periodId: UUID, apAccountId: UUID, cashAccountId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_post_ap_payment_journal_checked', {
    p_payment_id: paymentId,
    p_period_id: periodId,
    p_ap_account_id: apAccountId,
    p_cash_account_id: cashAccountId,
  });
  if (error) throw new Error(`fn_post_ap_payment_journal_checked failed: ${error.message}`);
  return data as UUID;
}
